import React, { useState } from 'react';
import { Modal, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { ScanLine } from './scanTypes';
import classes from './FindOnMenu.module.css';

const points = (q: number[]) => [0, 2, 4, 6].map((k) => `${q[k]},${q[k + 1]}`).join(' ');

interface FindOnMenuProps {
  photoUrl: string;
  line: ScanLine;
  /** Just the thumbnail, to sit beside a heading; otherwise a row with "Find on the menu" */
  compact?: boolean;
}

/**
 * Where the line is on the menu: a tiny thumbnail of the photo with the line
 * marked, opening the whole photo with the line circled.
 */
export const FindOnMenu: React.FC<FindOnMenuProps> = ({ photoUrl, line, compact }) => {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  // The line's box in the photo's pixels, and an ellipse through its corners
  const q = line.q;
  const xs = [q[0], q[2], q[4], q[6]].map((u) => u * (size?.w ?? 0));
  const ys = [q[1], q[3], q[5], q[7]].map((v) => v * (size?.h ?? 0));
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const rx = ((x1 - x0) / 2) * Math.SQRT2 + 10, ry = ((y1 - y0) / 2) * Math.SQRT2 + 10;

  return (
    <>
      <UnstyledButton className={compact ? classes.compact : classes.row} onClick={() => setOpen(true)} aria-label={compact ? 'Find on the menu' : undefined}>
        <span className={classes.thumb}>
          <img src={photoUrl} alt="" onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} />
          <svg viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <polygon points={points(q)} className={classes.mark} />
          </svg>
        </span>
        {!compact && (
          <>
            <Text component="span" fw={600} size="sm" flex={1}>Find on the menu</Text>
            <IconChevronRight size={18} />
          </>
        )}
      </UnstyledButton>

      <Modal opened={open} onClose={() => setOpen(false)} title={line.label || line.t} size="lg" zIndex={400}>
        <div className={classes.photo}>
          <img src={photoUrl} alt="The menu" />
          {size && (
            <svg viewBox={`0 0 ${size.w} ${size.h}`} aria-hidden="true">
              <ellipse cx={(x0 + x1) / 2} cy={(y0 + y1) / 2} rx={rx} ry={ry} className={classes.ring} strokeWidth={Math.max(size.w, size.h) * 0.006} />
            </svg>
          )}
        </div>
      </Modal>
    </>
  );
};
